import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase, type Profile, type Appointment, type Client } from '@/lib/supabase';

export default function CalendarPage() {
  const navigate = useNavigate();
  const [profile, setProfile] = useState<Profile | null>(null);
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    clientId: '',
    date: '',
    startTime: '',
    endTime: '',
    color: '#14B8A6',
  });

  const loadData = useCallback(async (userId: string) => {
    const [aptsRes, clientsRes] = await Promise.all([
      supabase
        .from('appointments')
        .select('*, client:clients(*)')
        .eq('user_id', userId)
        .order('start_time', { ascending: true }),
      supabase
        .from('clients')
        .select('*')
        .eq('user_id', userId)
        .order('full_name', { ascending: true }),
    ]);

    if (!aptsRes.error) setAppointments(aptsRes.data || []);
    if (!clientsRes.error) setClients(clientsRes.data || []);
  }, []);

  useEffect(() => {
    const init = async () => {
      const { data: { user: authUser } } = await supabase.auth.getUser();
      if (!authUser) {
        navigate('/login');
        return;
      }

      const { data: profileData } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', authUser.id)
        .maybeSingle();

      if (profileData) setProfile(profileData);
      await loadData(authUser.id);
    };

    init();
  }, [navigate, loadData]);

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    navigate('/');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError('');
    setIsSubmitting(true);

    try {
      const { data: { user: authUser } } = await supabase.auth.getUser();
      if (!authUser) throw new Error('Not authenticated');

      const startDateTime = new Date(`${formData.date}T${formData.startTime}`);
      const endDateTime = new Date(`${formData.date}T${formData.endTime}`);

      if (endDateTime <= startDateTime) {
        throw new Error('End time must be after start time');
      }

      const { error } = await supabase.from('appointments').insert([
        {
          user_id: authUser.id,
          client_id: formData.clientId || null,
          title: formData.title,
          description: formData.description || null,
          start_time: startDateTime.toISOString(),
          end_time: endDateTime.toISOString(),
          color: formData.color,
          status: 'scheduled',
        },
      ]);

      if (error) throw error;

      setIsModalOpen(false);
      setFormData({
        title: '',
        description: '',
        clientId: '',
        date: '',
        startTime: '',
        endTime: '',
        color: '#14B8A6',
      });
      await loadData(authUser.id);
    } catch (err) {
      console.error('Create appointment error:', err);
      setSubmitError(err instanceof Error ? err.message : 'Failed to create appointment');
    } finally {
      setIsSubmitting(false);
    }
  };

  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const days: (number | null)[] = [];
    for (let i = 0; i < firstDay; i++) days.push(null);
    for (let i = 1; i <= daysInMonth; i++) days.push(i);
    return days;
  };

  const getAppointmentsForDay = (day: number) => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    return appointments.filter((apt) => {
      const d = new Date(apt.start_time);
      return d.getDate() === day && d.getMonth() === month && d.getFullYear() === year;
    });
  };

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
  ];
  const days = getDaysInMonth(currentDate);
  const today = new Date();

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Navigation */}
      <nav className="bg-white border-b border-gray-200 h-16">
        <div className="max-w-7xl mx-auto px-6 h-full flex items-center justify-between">
          <span className="text-2xl font-bold text-gray-900">Appofy</span>
          <div className="flex items-center gap-6">
            <button onClick={() => navigate('/dashboard')} className="text-gray-600 hover:text-gray-900 transition-colors cursor-pointer whitespace-nowrap">
              Dashboard
            </button>
            <button onClick={() => navigate('/calendar')} className="text-teal-600 font-semibold cursor-pointer whitespace-nowrap">
              Calendar
            </button>
            <button onClick={() => navigate('/clients')} className="text-gray-600 hover:text-gray-900 transition-colors cursor-pointer whitespace-nowrap">
              Clients
            </button>
            <div className="flex items-center gap-3 pl-6 border-l border-gray-200">
              <div className="text-right">
                <div className="text-sm font-semibold text-gray-900">{profile?.full_name || 'User'}</div>
                <div className="text-xs text-gray-500">{profile?.business_name || 'Business Owner'}</div>
              </div>
              <button
                onClick={handleSignOut}
                className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center hover:bg-gray-200 transition-colors cursor-pointer"
              >
                <i className="ri-logout-box-line text-gray-600"></i>
              </button>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-1">Calendar</h1>
            <p className="text-gray-600">Schedule and manage appointments</p>
          </div>
          <button
            onClick={() => setIsModalOpen(true)}
            className="bg-teal-500 text-white px-5 py-2.5 rounded-lg hover:bg-teal-600 transition-all flex items-center gap-2 whitespace-nowrap cursor-pointer font-semibold"
          >
            <i className="ri-add-line text-lg"></i>
            New Appointment
          </button>
        </div>

        {/* Calendar Header */}
        <div className="bg-white rounded-xl border border-gray-200 p-5 mb-4 flex items-center justify-between">
          <h2 className="text-xl font-bold text-gray-900">
            {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
          </h2>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1))}
              className="w-9 h-9 border border-gray-300 rounded-lg flex items-center justify-center hover:border-teal-500 hover:text-teal-500 transition-all cursor-pointer"
            >
              <i className="ri-arrow-left-line"></i>
            </button>
            <button
              onClick={() => setCurrentDate(new Date())}
              className="px-4 py-2 border border-gray-300 rounded-lg hover:border-teal-500 hover:text-teal-500 transition-all text-sm whitespace-nowrap cursor-pointer"
            >
              Today
            </button>
            <button
              onClick={() => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1))}
              className="w-9 h-9 border border-gray-300 rounded-lg flex items-center justify-center hover:border-teal-500 hover:text-teal-500 transition-all cursor-pointer"
            >
              <i className="ri-arrow-right-line"></i>
            </button>
          </div>
        </div>

        {/* Calendar Grid */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="grid grid-cols-7 border-b border-gray-200">
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((d) => (
              <div key={d} className="p-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wide">
                {d}
              </div>
            ))}
          </div>
          <div className="grid grid-cols-7">
            {days.map((day, index) => {
              const dayApts = day ? getAppointmentsForDay(day) : [];
              const isToday =
                day &&
                day === today.getDate() &&
                currentDate.getMonth() === today.getMonth() &&
                currentDate.getFullYear() === today.getFullYear();

              return (
                <div
                  key={index}
                  className={`min-h-28 p-2 border-b border-r border-gray-100 ${!day ? 'bg-gray-50/50' : 'hover:bg-gray-50'} transition-colors`}
                >
                  {day && (
                    <>
                      <div
                        className={`text-sm font-semibold mb-1.5 w-7 h-7 flex items-center justify-center rounded-full ${
                          isToday ? 'bg-teal-500 text-white' : 'text-gray-700'
                        }`}
                      >
                        {day}
                      </div>
                      <div className="space-y-1">
                        {dayApts.slice(0, 2).map((apt) => (
                          <div
                            key={apt.id}
                            className="text-xs px-1.5 py-1 rounded cursor-pointer hover:opacity-80 transition-opacity"
                            style={{
                              backgroundColor: `${apt.color}20`,
                              borderLeft: `3px solid ${apt.color}`,
                            }}
                          >
                            <div className="font-semibold text-gray-900 truncate">{apt.title}</div>
                            <div className="text-gray-500 truncate">
                              {new Date(apt.start_time).toLocaleTimeString('en-US', {
                                hour: 'numeric',
                                minute: '2-digit',
                                hour12: true,
                              })}
                            </div>
                          </div>
                        ))}
                        {dayApts.length > 2 && (
                          <div className="text-xs text-gray-400 pl-1">+{dayApts.length - 2} more</div>
                        )}
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* New Appointment Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setIsModalOpen(false)}
          />
          <div className="relative bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-900">New Appointment</h2>
                <button
                  onClick={() => setIsModalOpen(false)}
                  className="w-9 h-9 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
                >
                  <i className="ri-close-line text-xl text-gray-600"></i>
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Title *</label>
                  <input
                    type="text"
                    required
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                    placeholder="e.g. Haircut, Consultation..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Description</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                    rows={2}
                    placeholder="Optional notes..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Client</label>
                  <select
                    value={formData.clientId}
                    onChange={(e) => setFormData({ ...formData, clientId: e.target.value })}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm cursor-pointer"
                  >
                    <option value="">No client selected</option>
                    {clients.map((client) => (
                      <option key={client.id} value={client.id}>
                        {client.full_name}
                      </option>
                    ))}
                  </select>
                  {clients.length === 0 && (
                    <p className="text-xs text-gray-400 mt-1">
                      No clients yet.{' '}
                      <button
                        type="button"
                        onClick={() => navigate('/clients')}
                        className="text-teal-600 underline cursor-pointer"
                      >
                        Add a client first
                      </button>
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Date *</label>
                  <input
                    type="date"
                    required
                    value={formData.date}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm cursor-pointer"
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Start Time *</label>
                    <input
                      type="time"
                      required
                      value={formData.startTime}
                      onChange={(e) => setFormData({ ...formData, startTime: e.target.value })}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm cursor-pointer"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">End Time *</label>
                    <input
                      type="time"
                      required
                      value={formData.endTime}
                      onChange={(e) => setFormData({ ...formData, endTime: e.target.value })}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm cursor-pointer"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Color</label>
                  <div className="flex gap-2">
                    {['#14B8A6', '#F97316', '#10B981', '#EC4899', '#EAB308', '#6366F1'].map((color) => (
                      <button
                        key={color}
                        type="button"
                        onClick={() => setFormData({ ...formData, color })}
                        className={`w-9 h-9 rounded-lg transition-all cursor-pointer ${
                          formData.color === color ? 'ring-2 ring-offset-2 ring-gray-700 scale-110' : ''
                        }`}
                        style={{ backgroundColor: color }}
                      />
                    ))}
                  </div>
                </div>

                {submitError && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center gap-2">
                    <i className="ri-error-warning-fill text-red-500"></i>
                    <p className="text-red-700 text-sm">{submitError}</p>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-teal-500 text-white py-3 rounded-lg font-semibold hover:bg-teal-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                >
                  {isSubmitting ? (
                    <span className="flex items-center justify-center gap-2">
                      <i className="ri-loader-4-line animate-spin"></i>
                      Creating...
                    </span>
                  ) : (
                    'Create Appointment'
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
